import { defineComponent as _defineComponent } from 'vue'
import { debounce } from 'lodash-es';
import { Ref, ref } from 'vue';

import { ScrollView } from '@/data/datatypes/online/ScrollView';

interface Props {
  scrollX?: boolean;
  scrollY?: boolean;
  autoScrollThreshold?: number;
}
interface Emits {
  (e: 'scrollX', value: number): void,
  (e: 'scrollY', value: number): void,
}
export interface ScrollableContentApi {
  scrollToX: (pixels: number) => void;
  scrollToY: (pixels: number) => void;
  getView: () => ScrollView;
  getContentWidth: () => number;
  getLastScrollLeft: () => number;
  getLastScrollTop: () => number;
  isAtBottom: () => boolean;
  scrollToBottom: () => void;
  scrollToRight: () => void;
  getRootElement: () => HTMLDivElement | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ScrollableContent',
  props: {
    scrollX: { type: Boolean, default: false },
    scrollY: { type: Boolean, default: true },
    autoScrollThreshold: { default: 32 }
  },
  emits: ["scrollX", "scrollY"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'scrollX', value: number): void,
  (e: 'scrollY', value: number): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { scrollX: boolean, scrollY: boolean, autoScrollThreshold: number }





const scroller: Ref<HTMLDivElement | null> = ref(null);
const content: Ref<HTMLDivElement | null> = ref(null);

const lastScrollTop: Ref<number> = ref(0);
const lastScrollLeft: Ref<number> = ref(0);

function scrollToBottom(): void {
  if (content.value) {
    scrollToY(content.value.scrollHeight);
  }
}

function scrollToRight(): void {
  if (content.value) {
    scrollToX(content.value.scrollWidth);
  }
}

function scrollToY(pixels: number): void {
  if (scroller.value && scroller.value.scrollTop !== pixels) {
    scroller.value.scrollTop = pixels;
  }
}

function getLastScrollTop(): number {
  return lastScrollTop.value;
}

function getLastScrollLeft(): number {
  return lastScrollLeft.value;
}

function scrollToX(pixels: number): void {
  if (scroller.value && scroller.value.scrollLeft !== pixels) {
    scroller.value.scrollLeft = pixels;
  }
}

function getContentWidth(): number {
  return content.value?.clientWidth ?? 0;
}

function isAtBottom(): boolean {
  if (!content.value || !scroller.value) {
    return false;
  }

  const netContentHeight: number = content.value.scrollHeight - scroller.value.scrollTop;
  const lowerThreshold: number = scroller.value.clientHeight - (props.autoScrollThreshold / 2);
  const upperThreshold: number = scroller.value.clientHeight + (props.autoScrollThreshold / 2);
  return netContentHeight >= lowerThreshold && netContentHeight <= upperThreshold;
}

function getView(): ScrollView {
  const scrollerElement = scroller.value;

  // These include padding and any scrollbar, and are the result after any CSS transforms
  const containerHeight = Math.round(scrollerElement?.getBoundingClientRect().height ?? 0);
  const containerWidth = Math.round(scrollerElement?.getBoundingClientRect().width ?? 0);
  // These refer to the scrollable content and will be larger than client or container if there is content that needs
  // to scroll
  const contentHeight = Math.round(scrollerElement?.scrollHeight ?? 0);
  const contentWidth = Math.round(scrollerElement?.scrollWidth ?? 0);
  const scrollTop = scrollerElement?.scrollTop ?? 0;
  const scrollLeft = scrollerElement?.scrollLeft ?? 0;

  return {
    containerHeight,
    containerWidth,
    contentHeight,
    contentWidth,
    scrollTop,
    scrollLeft,
  };
}

function onScrollYChanged(): number {
  emit('scrollY', scroller.value?.scrollTop ?? 0);
  return scroller.value?.scrollTop ?? 0;
}

// See: https://lodash.com/docs/4.17.15#debounce
const debouncedOnScrollYChanged = debounce(onScrollYChanged, 100, {
  maxWait: 150,
  leading: true,
  trailing: true,
});

function onScrollXChanged(): number {
  emit('scrollX', scroller.value?.scrollLeft ?? 0);
  return scroller.value?.scrollLeft ?? 0;
}

const debouncedOnScrollXChanged = debounce(onScrollXChanged, 100, {
  maxWait: 150,
  leading: true,
  trailing: true,
});

function handleScroll() {
  if (!scroller.value) {
    return;
  }
  if (lastScrollTop.value !== scroller.value.scrollTop) {
    debouncedOnScrollYChanged();
  }
  if (lastScrollLeft.value !== scroller.value.scrollLeft) {
    debouncedOnScrollXChanged();
  }
  lastScrollLeft.value = scroller.value.scrollLeft;
  lastScrollTop.value = scroller.value.scrollTop;
}

function getRootElement(): HTMLDivElement | null {
  return scroller.value;
}

expose({
  scrollToX,
  scrollToY,
  getView,
  getContentWidth,
  getLastScrollLeft,
  getLastScrollTop,
  isAtBottom,
  scrollToBottom,
  scrollToRight,
  getRootElement,
});

return { __sfc: true,props, emit, scroller, content, lastScrollTop, lastScrollLeft, scrollToBottom, scrollToRight, scrollToY, getLastScrollTop, getLastScrollLeft, scrollToX, getContentWidth, isAtBottom, getView, onScrollYChanged, debouncedOnScrollYChanged, onScrollXChanged, debouncedOnScrollXChanged, handleScroll, getRootElement }
}

})